// BuildingStatusBadge — three-state pill for the capture surface
// (buildspec 20-ui §5.4, PRD §7.2.1).
//
// Two render modes, chosen by whether a caller supplies `onToggle`:
//
//   1. Read-only (no `onToggle`) — list-card variant used by
//      SiteDetailPage and admin drill-downs. Rendered as a `<span>`
//      with `aria-label="Status: <label>"` so it reads as an advisory
//      status, not a control.
//   2. Interactive (`onToggle` supplied AND status is in_progress or
//      complete) — BuildingCaptureScreen variant. Rendered as a `<button>` that
//      opens a confirm `<Dialog>` (spec §5.4: "Mark as Complete?" /
//      "Reopen this building?"). Confirming invokes `onToggle`, which
//      the parent wires to a capture-client dispatch
//      (`completeSession` for in_progress → complete, `reopenSession`
//      for complete → in_progress).
//
// `not_started` is advisory-only per spec §5.4 — there is no session
// to complete or reopen yet, so even with `onToggle` supplied the
// component falls back to the read-only span. This keeps the
// responsibility for "don't dispatch against a missing session" inside
// this component rather than every caller. The admin-only statuses
// (to_be_demolished / out_of_scope) are likewise always read-only here:
// only the admin building edit form sets/clears them.
//
// The `<Dialog>` primitive handles focus trap, portal isolation, and
// Esc/overlay dismissal (see `ui/dialog.tsx`). We only own the trigger
// button and the confirm copy.
//
// Colour is never the sole signal: each status carries a Lucide icon
// plus an accessible label so assistive-tech users and colour-vision-
// impaired users still read status correctly (buildspec 20-ui §9).
//
// Offline precondition (spec §8.3): completeSession / reopenSession
// must be blocked offline. Callers pass `disabled` to disable the
// confirm button inside the dialog without closing it; the copy
// explains why the button is disabled. The badge trigger itself stays
// tappable so a user can inspect the explanation.

import { useState, type ReactElement } from 'react'
import { Check, Circle, CircleDot, CircleSlash, TriangleAlert } from 'lucide-react'

import type { BuildingStatus } from '@citycare/shared'

import { Button } from '../../ui/button'
import { Dialog } from '../../ui/dialog'

export interface BuildingStatusBadgeProps {
  status: BuildingStatus
  // Supplying this flips the badge into interactive mode. Confirm-dialog
  // direction is inferred from `status`: in_progress → complete,
  // complete → reopen. `not_started` stays read-only regardless.
  onToggle?: () => void
  // Disables the confirm button inside the dialog. The trigger itself
  // stays tappable so offline copy can still be surfaced (§8.3).
  disabled?: boolean
  // Optional override for the disabled-confirm copy. Defaults to the
  // generic offline message; callers with a richer reason pass it here.
  disabledReason?: string
}

interface StatusPresentation {
  label: string
  icon: typeof Circle
  // Tailwind classes for background + text. Chosen from the repo's
  // existing palette (see `ui/badge.tsx`) so badges across admin and
  // capture surfaces stay visually consistent.
  className: string
}

const PRESENTATION: Record<BuildingStatus, StatusPresentation> = {
  not_started: {
    label: 'Not started',
    icon: Circle,
    // Spring Wood bg, Firefly text, dotted gray-300 border — conveys "blank slate"
    className:
      'bg-[color:var(--color-surface)] text-[color:var(--color-brand-deep)] border border-dotted border-gray-300',
  },
  in_progress: {
    label: 'In progress',
    icon: CircleDot,
    // Amber kept intentionally — industry-recognised "in progress" signal (§W3.4)
    className: 'bg-amber-50 text-amber-800',
  },
  complete: {
    label: 'Completed',
    icon: Check,
    // Inch Worm bg + Firefly text — on-brand "complete" signal (§W3.4)
    className:
      'bg-[color:var(--color-brand)] text-[color:var(--color-brand-foreground)]',
  },
  to_be_demolished: {
    label: 'To be Demolished',
    icon: TriangleAlert,
    // Red reads as "slated for removal" — admin-set, advisory only here.
    className: 'bg-red-50 text-red-800',
  },
  out_of_scope: {
    label: 'Out of Scope',
    icon: CircleSlash,
    // Neutral grey — the building is simply not part of the capture scope.
    className: 'bg-gray-100 text-gray-700',
  },
}

const READ_ONLY_CLASSES =
  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium'

// Interactive trigger is sized to meet the 44 px touch-target
// invariant (AGENTS.md invariant #10). Focus ring uses --color-brand-deep
// per §W3.4. Read-only pills stay small because they are not controls.
const INTERACTIVE_CLASSES =
  'inline-flex min-h-11 items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-brand-deep)] disabled:cursor-not-allowed disabled:opacity-50'

interface DialogCopy {
  title: string
  description: string
  confirmLabel: string
}

const DIALOG_COPY: Record<'complete' | 'reopen', DialogCopy> = {
  complete: {
    title: 'Mark as Complete?',
    description:
      'Marking this building as complete locks its counts from further edits until it is reopened.',
    confirmLabel: 'Mark complete',
  },
  reopen: {
    title: 'Reopen this building?',
    description:
      'Reopening lets you edit counts again. The building status returns to In progress.',
    confirmLabel: 'Reopen',
  },
}

export function BuildingStatusBadge({
  status,
  onToggle,
  disabled = false,
  disabledReason,
}: BuildingStatusBadgeProps): ReactElement {
  const { label, icon: Icon, className } = PRESENTATION[status]
  // Interactive mode is limited to the two field-toggleable statuses.
  // `not_started` has no session to toggle; the admin-only statuses
  // (to_be_demolished / out_of_scope) are never field-toggleable — only
  // the admin building edit form sets/clears them.
  const isInteractive =
    onToggle !== undefined &&
    (status === 'in_progress' || status === 'complete')
  const [dialogOpen, setDialogOpen] = useState(false)

  if (!isInteractive) {
    return (
      <span
        className={[READ_ONLY_CLASSES, className].join(' ')}
        aria-label={`Status: ${label}`}
        data-status={status}
      >
        <Icon aria-hidden="true" className="h-3.5 w-3.5" />
        {label}
      </span>
    )
  }

  const copy =
    status === 'complete' ? DIALOG_COPY.reopen : DIALOG_COPY.complete

  const handleConfirm = (): void => {
    if (disabled) return
    setDialogOpen(false)
    onToggle()
  }

  const handleClose = (): void => {
    setDialogOpen(false)
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setDialogOpen(true)}
        className={[INTERACTIVE_CLASSES, className].join(' ')}
        aria-label={`Status: ${label}. Tap to change.`}
        aria-haspopup="dialog"
        aria-expanded={dialogOpen}
        data-status={status}
        data-interactive="true"
      >
        <Icon aria-hidden="true" className="h-3.5 w-3.5" />
        {label}
      </button>
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        title={copy.title}
        description={copy.description}
      >
        {disabled && disabledReason !== undefined ? (
          <p className="text-sm text-amber-700" data-testid="status-toggle-disabled-reason">
            {disabledReason}
          </p>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={disabled}
          >
            {copy.confirmLabel}
          </Button>
        </div>
      </Dialog>
    </>
  )
}
